import { useMemo } from 'react';
import { useTranslate } from 'ra-core';
import { type SpreadsheetColumns } from '../../utils/spreadsheet/save';
import { type CsvRecord } from './types';
import { Info } from 'lucide-react';
import { type ZodCsvSchema, getZodCsvRowObjectSchema } from './bulkZodUtils';
import { BulkOperationDialogFooter } from './BulkOperationDialogFooter';
import { ExportSheetButton } from './ExportSheetButton';
import { Button } from '../../components/ui/button';

interface BulkOperationDialogGetTemplateProps<T extends CsvRecord> {
  label: string;
  resource: string;
  onBack: () => void;
  onNext: () => void;
  /**
   * Makes sure that the properties in this schema will be included in the exported template.
   */
  schema?: ZodCsvSchema<T>;
  /**
   * Example values to be included in the exported template.
   */
  exampleValues?: Partial<T>[];
}

export function BulkOperationDialogGetTemplate<T extends CsvRecord>({
  onBack,
  onNext,
  label,
  resource,
  schema,
  exampleValues,
}: BulkOperationDialogGetTemplateProps<T>) {
  const translate = useTranslate();

  const keys = useMemo(() => {
    if (!schema) return undefined;

    const rowSchema = getZodCsvRowObjectSchema(schema);
    return Object.keys(rowSchema.shape) as SpreadsheetColumns<T>;
  }, [schema]);

  return (
    <>
      <div className="space-y-5 p-6">
        <ol className="list-inside list-decimal space-y-4 leading-7">
          <li>
            <div className="flex flex-wrap items-center gap-2">
              <span>
                <strong>
                  {translate('moonstar.bulk_operation.template.download', {
                    _: 'Download',
                  })}
                </strong>{' '}
                {translate('moonstar.bulk_operation.template.theTemplate', {
                  _: 'the template',
                })}
              </span>
              <ExportSheetButton<Partial<T>>
                label="moonstar.user.exportTemplate.button"
                getSheet={() => {
                  return {
                    basename: `${translate(label)} - Template`,
                    columns: keys,
                    data: exampleValues,
                  };
                }}
              />
            </div>
          </li>
          <li>
            <span>
              {translate('moonstar.bulk_operation.template.addRecords', {
                resource: translate(`resources.${resource}.name`, { count: 2 }),
                _: `Add the ${translate(`resources.${resource}.name`, {
                  count: 2,
                })} you'd like to process`,
              })}
            </span>
          </li>
          <li>
            <span>
              {translate('moonstar.bulk_operation.template.saveAs', {
                _: 'Save as an Excel or CSV file on your computer',
              })}
            </span>
          </li>
        </ol>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Info className="size-4 shrink-0" />
          <span>
            {translate('moonstar.bulk_operation.template.dateFormat', {
              _: 'Dates will be processed using the format DD/MM/YYYY',
            })}
          </span>
        </div>
      </div>
      <BulkOperationDialogFooter>
        <Button type="button" variant="secondary" onClick={onBack}>
          {translate('moonstar.bulk_operation.actions.cancel', {
            _: 'Cancel',
          })}
        </Button>
        <Button type="button" onClick={onNext}>
          {translate('moonstar.bulk_operation.actions.next', { _: 'Next' })}
        </Button>
      </BulkOperationDialogFooter>
    </>
  );
}
